import React, { useEffect, Suspense } from 'react';
import { UserManager } from 'paycor-oidc-client';
import { Security } from 'paycor-react-oidc';
import {
	AuthenticationProvider,
	GlobalNavigation,
	LoadingStatus,
	SessionTimeout
} from '@paycor/pacl';
import { onAuthFailed, onAuthError } from 'utils/authentication';
import RootRoutes from 'RootRoutes';
import {
	LoginUserProvider,
	useLoginUserDispatch,
	LoginActions
} from 'hooks/loginUserContext';
import config from 'config';
import { TabDataProvider } from 'hooks/tabDataContext';
import { TranslationProvider } from 'hooks/translationContext';

const { SECURITYSETTINGS, HOST, AUTHPROVIDERSETTINGS } = config;
const { BASE_HCM_URL, APIM_SUBSCRIPTION_KEY } = HOST;

const isLocal = process.env.NODE_ENV !== 'production';
const refreshUrl = isLocal ? `/accounts/` : `${BASE_HCM_URL}/accounts/`;

const App = () => {
	return (
		<Suspense fallback={<LoadingStatus />}>
			<Security
				settings={SECURITYSETTINGS}
				consoleLogsEnabled={false}
				onUnAuthorizedCallback={onAuthFailed}
				onAuthErrorCallback={onAuthError}
			>
				<AuthenticationProvider settings={AUTHPROVIDERSETTINGS}>
					<SessionTimeout
						applicationUrl={`${BASE_HCM_URL}`}
						refreshUrl={refreshUrl}
						APIm={APIM_SUBSCRIPTION_KEY}
						useLocal={isLocal}
					>
						<LoginUserProvider>
							<TabDataProvider>
								<GlobalNavigation hcmDomain={BASE_HCM_URL} useMockHeader={isLocal} />
								<UserLoader>
									<TranslationProvider>
										<RootRoutes />
									</TranslationProvider>
								</UserLoader>
							</TabDataProvider>
						</LoginUserProvider>
					</SessionTimeout>
				</AuthenticationProvider>
			</Security>
		</Suspense>
	);
};

// A component that will load the user from the security context.
const UserLoader = ({ children }) => {
	const dispatch = useLoginUserDispatch();

	useEffect(() => {
		const fileImportAdminUserPriv = 966;
		const mapRegistrationPriv = 960;
		const isPaycorUserPriv = 401;
		const skipDefaultTaxesAccessPriv = 7387;
		const disableAutoCancelPriv = 2237;

		const fetchLoginUser = async () => {
			let userId = '';
			let manager = new UserManager(SECURITYSETTINGS);
			let user = await manager.getUser();
			if (Boolean(user)) {
				let userFromToken = await manager.getUserByAccessToken(user.access_token);
				if (
					Boolean(userFromToken) &&
					Boolean(userFromToken.profile) &&
					Boolean(userFromToken.profile.sub)
				) {
					userId = userFromToken.profile.sub;

					dispatch({
						action: LoginActions.setUser,
						payload: {
							user: userFromToken.profile,
							userId: userId,
							isAdmin: userFromToken.hasPrivilege(fileImportAdminUserPriv),
							canRegisterMap: userFromToken.hasPrivilege(mapRegistrationPriv),
							isPaycor: userFromToken.hasPrivilege(isPaycorUserPriv),
							canSkipTaxes:
								userFromToken.hasPrivilege(skipDefaultTaxesAccessPriv) ||
								userFromToken.hasPrivilege(fileImportAdminUserPriv),
							canDisableAutoCancel:
								userFromToken.hasPrivilege(disableAutoCancelPriv) ||
								userFromToken.hasPrivilege(fileImportAdminUserPriv)
						}
					});
				}
			}
		};

		fetchLoginUser();
	}, [dispatch]);

	return <>{children}</>;
};

export default App;
